import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, X } from 'lucide-react';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DetailData, SkillData, useApiClient } from '../../../lib/api';
import { monthInputToApiDate } from '@/lib/profile-dates';

type StateSetter<T> = Dispatch<SetStateAction<T>>;


// Types
export interface EducationData {
    id?: string;
    user_id?: string;
    institution: string;
    degree_type: string;
    major: string;
    start_date: string;
    end_date?: string | null;
    is_ongoing?: boolean;
}

// Props interface
interface EducationTabProps {
    educations?: EducationData[];
    setEducations?: StateSetter<EducationData[]>
    details?: { [educationId: string]: DetailData[] };
    setDetails?: StateSetter<{ [educationId: string]: DetailData[] }>
    skills?: { [educationId: string]: SkillData[] };
    setSkills?: StateSetter<{ [educationId: string]: SkillData[] }>
}

const DEGREE_OPTIONS = [
    "Associate's", "Bachelor's", "Master's", "PhD", "MBA", "JD", "MD", "Certificate", "Diploma", "High School", "GED", "Other"
];

import EducationForm from './EducationForm';
import EducationList from './EducationList';

// Loading skeleton component
const EducationLoadingSkeleton: React.FC = () => (
    <div className="space-y-6 animate-pulse">
        {[1, 2].map((i) => (
            <div key={i} className="relative border-l-2 border-gray-200 pl-4 sm:pl-6 pb-6">
                <div className="absolute w-3 h-3 bg-gray-300 rounded-full -left-[7px] top-[10px]"></div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0 space-y-2">
                        <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="mt-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-8 bg-gray-100 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="flex flex-wrap gap-2">
                        <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-14"></div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export const EducationTab: React.FC<EducationTabProps> = ({
    educations,
    setEducations,
    details,
    setDetails,
    skills,
    setSkills,
}) => {
    const { api } = useApiClient();

    const [internalEducationList, setInternalEducationList] = useState<EducationData[]>([]);
    const [internalEducationDetails, setInternalEducationDetails] = useState<{ [educationId: string]: DetailData[] }>({});
    const [internalEducationSkills, setInternalEducationSkills] = useState<{ [educationId: string]: SkillData[] }>({});

    const educationList = internalEducationList ?? educations;
    const educationDetails = internalEducationDetails ?? details;
    const educationSkills = internalEducationSkills ?? skills;

    const setEducationList = setEducations ?? setInternalEducationList;
    const setEducationDetails = setDetails ?? setInternalEducationDetails;
    const setEducationSkills = setSkills ?? setInternalEducationSkills;


    // State for form and list
    const [showAddEducation, setShowAddEducation] = useState(false);
    const [isOtherDegree, setIsOtherDegree] = useState(false);
    const [education, setEducation] = useState<EducationData>({
        institution: '',
        degree_type: '',
        major: '',
        start_date: '',
        end_date: '',
        is_ongoing: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Handlers for EducationForm
    const handleEducationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEducation(prev => ({ ...prev, [name]: value }));
    };
    const handleDegreeChange = (value: string) => {
        if (value === 'Other') {
            setIsOtherDegree(true);
            setEducation(prev => ({ ...prev, degree_type: '' }));
        } else {
            setIsOtherDegree(false);
            setEducation(prev => ({ ...prev, degree_type: value }));
        }
    };
    const resetDegreeDropdown = () => {
        setIsOtherDegree(false);
        setEducation(prev => ({ ...prev, degree_type: '' }));
    };
    const handleIsOngoingChange = (checked: boolean) => {
        setEducation(prev => ({
            ...prev,
            is_ongoing: checked,
            end_date: checked ? undefined : prev.end_date,
        }));
    };
    const handleAddEducation = async () => {
        if (!education.institution || !education.degree_type || !education.major) {
            toast.error('Please fill in required education fields');
            return;
        }
        setIsLoading(true);

        // Write the education to our api
        try {
            await api.createEducation({
                ...education,
                start_date: monthInputToApiDate(education.start_date),
                end_date: education.is_ongoing ? undefined : monthInputToApiDate(education.end_date),
            });
            
            // Refetch all educations to get the latest data from the server
            const educations = await api.getEducations();
            setEducationList(educations);
            
            // Fetch details and skills for all educations
            const detailsObj: { [educationId: string]: DetailData[] } = {};
            const skillsObj: { [educationId: string]: SkillData[] } = {};
            await Promise.all(
                educations.map(async (edu: EducationData) => {
                    if (!edu.id) return;
                    try {
                        const details = await api.getDetailsByEducationId(edu.id);
                        detailsObj[edu.id] = details;
                    } catch (err) {
                        console.error(`[Education] Failed to fetch details for ${edu.id}`, err);
                        detailsObj[edu.id] = [];
                    }
                    try {
                        const skills = await api.getSkillsByEducationId(edu.id);
                        skillsObj[edu.id] = skills;
                    } catch (err) {
                        console.error(`[Education] Failed to fetch skills for ${edu.id}`, err);
                        skillsObj[edu.id] = [];
                    }
                })
            );
            setEducationDetails(detailsObj);
            setEducationSkills(skillsObj);

            setEducation({
                institution: '', degree_type: '', major: '', start_date: '', end_date: '', is_ongoing: false
            });
            setIsOtherDegree(false);
            setShowAddEducation(false);
        } catch (err) {
            console.error('[Education] Failed to add education', err);
        } finally {
            setIsLoading(false);
        }
    };
    const handleCancelAdd = () => {
        setEducation({
            institution: '', degree_type: '', major: '', start_date: '', end_date: '', is_ongoing: false
        });
        setIsOtherDegree(false);
        setShowAddEducation(false);
    };

    useEffect(() => {
        const fetchEducations = async () => {
            try {
                setIsInitialLoading(true);
                console.log('[Education] Fetching education list...');
                const educations = await api.getEducations();
                setEducationList(educations);
                // Fetch details and skills for each education
                const detailsObj: { [educationId: string]: DetailData[] } = {};
                const skillsObj: { [educationId: string]: SkillData[] } = {};
                await Promise.all(
                    educations.map(async (edu: EducationData) => {
                        if (!edu.id) return;
                        try {
                            const details = await api.getDetailsByEducationId(edu.id);
                            detailsObj[edu.id] = details;
                        } catch (err) {
                            console.error(`[Education] Failed to fetch details for ${edu.id}`, err);
                            detailsObj[edu.id] = [];
                        }
                        try {
                            const skills = await api.getSkillsByEducationId(edu.id);
                            skillsObj[edu.id] = skills;
                        } catch (err) {
                            console.error(`[Education] Failed to fetch skills for ${edu.id}`, err);
                            skillsObj[edu.id] = [];
                        }
                    })
                );
                setEducationDetails(detailsObj);
                setEducationSkills(skillsObj);
            } catch (err) {
                console.error('[Education] Failed to fetch education list', err);
            } finally {
                setIsInitialLoading(false);
            }
        }
        fetchEducations();
    }, []);


    // UI rendering
    return (
        <Card className="w-full">
            <CardHeader className="flex flex-col sm:flex-row items-center sm:items-center justify-between space-y-2 sm:space-y-0 pb-4">
                <div className="text-center sm:text-left">
                    <CardTitle className="text-lg sm:text-xl">Education History</CardTitle>
                </div>
                <Button
                    onClick={() => setShowAddEducation(!showAddEducation)}
                    variant={showAddEducation ? "outline" : "default"}
                    size="icon"
                    className={showAddEducation ? "h-10 w-10 rounded-full sm:ml-auto" : "h-10 w-10 rounded-full bg-interview-blue text-white hover:bg-interview-blue/90 sm:ml-auto"}
                    aria-label={showAddEducation ? "Cancel adding education" : "Add education"}
                >
                    {showAddEducation ? <X className="h-4 w-4" /> : <Plus className="h-5 w-5" />}
                </Button>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
                {showAddEducation && (
                    <EducationForm
                        education={education}
                        isOtherDegree={isOtherDegree}
                        isLoading={isLoading}
                        onChange={handleEducationChange}
                        onDegreeChange={handleDegreeChange}
                        onResetDegreeDropdown={resetDegreeDropdown}
                        onIsOngoingChange={handleIsOngoingChange}
                        onSubmit={handleAddEducation}
                        onCancel={handleCancelAdd}
                    />
                )}
                {!showAddEducation && (
                    isInitialLoading ? (
                        <EducationLoadingSkeleton />
                    ) : (
                        <EducationList
                            educations={educationList}
                            setEducations={(newEduList) => setEducationList(newEduList)}
                            details={educationDetails}
                            setDetails={(newDetails) => setEducationDetails(newDetails)}
                            skills={educationSkills}
                            setSkills={(newSkills) => setEducationSkills(newSkills)}
                            loading={isLoading}
                            setLoading={setIsLoading}
                        />
                    )
                )}
            </CardContent>
        </Card>
    );
};

export default EducationTab;
